/**
 * Store Header Component
 * Displays store logo, Products/Categories toggle, cart button, and language selector
 * Matches Telegram Wallet MiniApp style
 */

import React, { useState, useContext, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { TranslationContext, useTranslation } from '../../hooks/useTranslation';
import LanguageDropdown from './LanguagePickerModal';

type ViewMode = 'products' | 'categories';

interface StoreHeaderProps {
  storeLogo?: string | null;
  storeLogoType?: 'image' | 'emoji';
  storeLogoEmoji?: string;
  cartCount?: number;
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
}

const StoreHeader: React.FC<StoreHeaderProps> = ({
  storeLogo,
  storeLogoType = 'emoji',
  storeLogoEmoji = '🛍️',
  cartCount = 0,
  viewMode = 'products',
  onViewModeChange
}) => {
  const navigate = useNavigate();
  const { botUsername } = useParams<{ botUsername: string }>();
  const [showLanguagePicker, setShowLanguagePicker] = useState(false);
  const languageButtonRef = useRef<HTMLButtonElement>(null);
  const translationContext = useContext(TranslationContext);
  const { t } = useTranslation();

  // Get current language flag
  const currentLang = translationContext?.availableLanguages.find(
    l => l.code === translationContext.language
  );
  const currentFlag = currentLang?.flag || '🌐';

  const handleCartClick = () => {
    navigate(`/shop/${botUsername}/cart`);
  };

  const handleLanguageClick = () => {
    setShowLanguagePicker(true);
    window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('light');
  };

  const handleTabClick = (mode: ViewMode) => {
    if (onViewModeChange) {
      onViewModeChange(mode);
    }
    window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('light');
  };

  return (
    <HeaderContainer>
      <HeaderContent>
        {/* Left: Store Avatar/Logo */}
        <AvatarSection>
          {storeLogoType === 'image' && storeLogo ? (
            <AvatarImage src={storeLogo} alt="Store" />
          ) : (
            <AvatarIcon>{storeLogoEmoji}</AvatarIcon>
          )}
        </AvatarSection>

        {/* Center: Products/Categories Toggle */}
        <TabSystem>
          <TabButton
            $active={viewMode === 'products'}
            onClick={() => handleTabClick('products')}
          >
            {t('shop.products')}
          </TabButton>
          <TabButton
            $active={viewMode === 'categories'}
            onClick={() => handleTabClick('categories')}
          >
            {t('shop.categories')}
          </TabButton>
        </TabSystem>

        {/* Right: Cart & Language Buttons */}
        <RightSection>
          <CartButton onClick={handleCartClick}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 22C9.55228 22 10 21.5523 10 21C10 20.4477 9.55228 20 9 20C8.44772 20 8 20.4477 8 21C8 21.5523 8.44772 22 9 22Z" fill="currentColor"/>
              <path d="M20 22C20.5523 22 21 21.5523 21 21C21 20.4477 20.5523 20 20 20C19.4477 20 19 20.4477 19 21C19 21.5523 19.4477 22 20 22Z" fill="currentColor"/>
              <path d="M1 1H5L7.68 14.39C7.77 14.8504 8.02 15.264 8.38 15.5583C8.74 15.8526 9.19 16.0084 9.66 16H19.4C19.8658 16.0016 20.3179 15.8418 20.6775 15.5469C21.0372 15.252 21.2821 14.8398 21.37 14.38L23 6H6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {cartCount > 0 && <CartBadge>{cartCount > 99 ? '99+' : cartCount}</CartBadge>}
          </CartButton>
          <LanguageButtonWrapper>
            <LanguageButton ref={languageButtonRef} onClick={handleLanguageClick}>
              <FlagEmoji>{currentFlag}</FlagEmoji>
            </LanguageButton>
            <LanguageDropdown
              isOpen={showLanguagePicker}
              onClose={() => setShowLanguagePicker(false)}
              anchorRef={languageButtonRef}
            />
          </LanguageButtonWrapper>
        </RightSection>
      </HeaderContent>
    </HeaderContainer>
  );
};

const HeaderContainer = styled.header`
  position: sticky;
  top: 0;
  background: var(--tg-theme-bg-color, #1a1a1a);
  z-index: 100;
  padding: 8px 16px 12px;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const AvatarSection = styled.div`
  flex-shrink: 0;
`;

const AvatarImage = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 20px;
  object-fit: cover;
`;

const AvatarIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 20px;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
`;

const TabSystem = styled.div`
  display: flex;
  background: #48484a;
  border-radius: 16px;
  padding: 2px;
  gap: 2px;
`;

const TabButton = styled.button<{ $active: boolean }>`
  padding: 8px 20px;
  border: none;
  background: ${props => props.$active ? 'var(--tg-theme-bg-color, #1a1a1a)' : 'transparent'};
  color: ${props => props.$active ? 'var(--tg-theme-text-color, #ffffff)' : 'var(--tg-theme-hint-color, #8e8e93)'};
  font-size: 15px;
  font-weight: 500;
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:active {
    transform: scale(0.95);
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const CartButton = styled.button`
  background: none;
  border: none;
  color: var(--tg-theme-hint-color, #8e8e93);
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: background-color 0.2s ease;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const CartBadge = styled.span`
  position: absolute;
  top: 2px;
  right: 2px;
  background: var(--tg-theme-link-color, #00ff88);
  color: var(--tg-theme-bg-color, #1a1a1a);
  font-size: 10px;
  font-weight: 600;
  padding: 2px 5px;
  border-radius: 10px;
  min-width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
`;

const LanguageButtonWrapper = styled.div`
  position: relative;
`;

const LanguageButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 8px;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const FlagEmoji = styled.span`
  font-size: 22px;
  line-height: 1;
`;

export default StoreHeader;
